import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Plus, Save, Eye } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../contexts/ToastContext'
import AdminLayout from '../../components/admin/AdminLayout'
import ModuleEditor from '../../components/course-editor/ModuleEditor'
import ModuleModal from '../../components/course-editor/ModuleModal'
import LessonModal from '../../components/course-editor/LessonModal'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Course {
  id: string
  title: string
  description: string
}

interface Module {
  id: string
  course_id: string
  title: string
  description: string | null
  order_index: number
  duration_minutes: number | null
  is_published: boolean
  lessons?: Lesson[]
}

interface Lesson {
  id: string
  module_id: string
  title: string
  content: string | null
  video_url: string | null
  resources: any
  order_index: number
  duration_minutes: number | null
  is_published: boolean
}

export default function CourseContentEditor() {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()
  
  const [course, setCourse] = useState<Course | null>(null)
  const [modules, setModules] = useState<Module[]>([])
  const [loading, setLoading] = useState(true)
  const [showModuleModal, setShowModuleModal] = useState(false)
  const [showLessonModal, setShowLessonModal] = useState(false)
  const [editingModule, setEditingModule] = useState<Module | null>(null)
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null)
  const [selectedModuleId, setSelectedModuleId] = useState<string>('')

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    if (courseId) {
      loadCourse()
      loadModules()
    }
  }, [courseId])

  const loadCourse = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('id, title, description')
        .eq('id', courseId)
        .single()

      if (error) throw error
      setCourse(data)
    } catch (error: any) {
      console.error('Error loading course:', error)
      showToast('error', 'Error al cargar el curso')
      navigate('/admin/cursos')
    }
  }

  const loadModules = async () => {
    setLoading(true)
    try {
      // Cargar módulos
      const { data: modulesData, error: modulesError } = await supabase
        .from('course_modules')
        .select('*')
        .eq('course_id', courseId)
        .order('order_index', { ascending: true })

      if (modulesError) throw modulesError

      // Cargar lecciones para cada módulo
      const { data: lessonsData, error: lessonsError } = await supabase
        .from('course_lessons')
        .select('*')
        .in('module_id', modulesData.map(m => m.id))
        .order('order_index', { ascending: true })

      if (lessonsError) throw lessonsError

      // Agrupar lecciones por módulo
      const modulesWithLessons = modulesData.map(module => ({
        ...module,
        lessons: lessonsData.filter(lesson => lesson.module_id === module.id)
      }))

      setModules(modulesWithLessons)
    } catch (error: any) {
      console.error('Error loading modules:', error)
      showToast('error', 'Error al cargar módulos y lecciones')
    } finally {
      setLoading(false)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = modules.findIndex(m => m.id === active.id)
    const newIndex = modules.findIndex(m => m.id === over.id)

    const newModules = arrayMove(modules, oldIndex, newIndex)
    
    // Actualizar order_index
    const updatedModules = newModules.map((module, index) => ({
      ...module,
      order_index: index
    }))

    setModules(updatedModules)

    // Guardar en base de datos
    try {
      const updates = updatedModules.map(module => 
        supabase
          .from('course_modules')
          .update({ order_index: module.order_index })
          .eq('id', module.id)
      )

      await Promise.all(updates)
      showToast('success', 'Orden de módulos actualizado')
    } catch (error: any) {
      console.error('Error updating module order:', error)
      showToast('error', 'Error al actualizar orden de módulos')
      loadModules() // Recargar en caso de error
    }
  }

  const handleAddModule = () => {
    setEditingModule(null)
    setShowModuleModal(true)
  }

  const handleEditModule = (module: Module) => {
    setEditingModule(module)
    setShowModuleModal(true)
  }

  const handleDeleteModule = async (moduleId: string) => {
    setModules(modules.filter(m => m.id !== moduleId))
  }

  const handleToggleModulePublish = (moduleId: string, isPublished: boolean) => {
    setModules(modules.map(m => 
      m.id === moduleId ? { ...m, is_published: isPublished } : m
    ))
  }

  const handleSaveModule = (module: Module) => {
    if (editingModule) {
      setModules(modules.map(m => m.id === module.id ? module : m))
    } else {
      setModules([...modules, { ...module, lessons: [] }])
    }
  }

  const handleAddLesson = (moduleId: string) => {
    setSelectedModuleId(moduleId)
    setEditingLesson(null)
    setShowLessonModal(true)
  }

  const handleEditLesson = (lesson: Lesson) => {
    setSelectedModuleId(lesson.module_id)
    setEditingLesson(lesson)
    setShowLessonModal(true)
  }

  const handleDeleteLesson = async (lessonId: string) => {
    try {
      const { error } = await supabase
        .from('course_lessons')
        .delete()
        .eq('id', lessonId)

      if (error) throw error

      // Actualizar estado local
      setModules(modules.map(module => ({
        ...module,
        lessons: module.lessons?.filter(l => l.id !== lessonId) || []
      })))

      showToast('success', 'Lección eliminada exitosamente')
    } catch (error: any) {
      console.error('Error deleting lesson:', error)
      const errorMessage = 'Error al eliminar lección: ' + error.message
      showToast('error', errorMessage)
    }
  }

  const handleSaveLesson = (lesson: Lesson) => {
    setModules(modules.map(module => {
      if (module.id === lesson.module_id) {
        if (editingLesson) {
          // Actualizar lección existente
          return {
            ...module,
            lessons: module.lessons?.map(l => l.id === lesson.id ? lesson : l) || [lesson]
          }
        } else {
          // Añadir nueva lección
          return {
            ...module,
            lessons: [...(module.lessons || []), lesson]
          }
        }
      }
      return module
    }))
  }

  const stats = {
    totalModules: modules.length,
    publishedModules: modules.filter(m => m.is_published).length,
    totalLessons: modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0),
    publishedLessons: modules.reduce((acc, m) => 
      acc + (m.lessons?.filter(l => l.is_published).length || 0), 0
    )
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-education mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando editor de contenido...</p>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin/cursos"
            className="inline-flex items-center text-education hover:text-education/80 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Gestión de Cursos
          </Link>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{course?.title}</h1>
              <p className="text-gray-600 mt-1">Editor de Contenido del Curso</p>
            </div>
            <Link
              to={`/cursos/${courseId}`}
              className="px-4 py-2 bg-action text-white rounded-lg hover:bg-action/90 flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Vista Previa
            </Link>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Módulos Totales</p>
            <p className="text-2xl font-bold text-gray-900">{stats.totalModules}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Módulos Publicados</p>
            <p className="text-2xl font-bold text-green-600">{stats.publishedModules}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Lecciones Totales</p>
            <p className="text-2xl font-bold text-gray-900">{stats.totalLessons}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Lecciones Publicadas</p>
            <p className="text-2xl font-bold text-green-600">{stats.publishedLessons}</p>
          </div>
        </div>

        {/* Botón Añadir Módulo */}
        <div className="mb-6">
          <button
            onClick={handleAddModule}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-education rounded-lg text-education hover:bg-education hover:text-white transition-colors"
          >
            <Plus className="h-5 w-5" />
            Añadir Nuevo Módulo
          </button>
        </div>

        {/* Lista de Módulos con Drag and Drop */}
        {modules.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={modules.map(m => m.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {modules.map((module) => (
                  <SortableModuleItem
                    key={module.id}
                    module={module}
                    onEdit={handleEditModule}
                    onDelete={handleDeleteModule}
                    onTogglePublish={handleToggleModulePublish}
                    onAddLesson={handleAddLesson}
                    onEditLesson={handleEditLesson}
                    onDeleteLesson={handleDeleteLesson}
                    onReorderLessons={() => {}}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-600 mb-2">No hay módulos creados aún</p>
            <p className="text-sm text-gray-500">Haz clic en "Añadir Nuevo Módulo" para empezar</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <ModuleModal
        isOpen={showModuleModal}
        onClose={() => setShowModuleModal(false)}
        onSave={handleSaveModule}
        courseId={courseId!}
        module={editingModule}
        maxOrderIndex={modules.length}
      />

      <LessonModal
        isOpen={showLessonModal}
        onClose={() => setShowLessonModal(false)}
        onSave={handleSaveLesson}
        courseId={courseId!}
        moduleId={selectedModuleId}
        lesson={editingLesson}
        maxOrderIndex={
          modules.find(m => m.id === selectedModuleId)?.lessons?.length || 0
        }
      />
    </AdminLayout>
  )
}

interface SortableModuleItemProps {
  module: Module
  onEdit: (module: Module) => void
  onDelete: (moduleId: string) => void
  onTogglePublish: (moduleId: string, isPublished: boolean) => void
  onAddLesson: (moduleId: string) => void
  onEditLesson: (lesson: Lesson) => void
  onDeleteLesson: (lessonId: string) => void
  onReorderLessons: (moduleId: string, lessons: Lesson[]) => void
}

function SortableModuleItem(props: SortableModuleItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: props.module.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ModuleEditor
        {...props}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}
